import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, SectionHeader, EnhancedSectionTitle, SectionSubtitle } from '../../styles/sharedComponents';
import { useFadeInOnScroll } from '../../../hooks/useFadeInOnScroll';
import FeaturedDisplay from './FeaturedDisplay';
import FeaturesList from './FeaturesList';
import { FeaturesSection, PowerfulFeaturesContainer } from './styles';

const Features: React.FC = () => {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  const [featuresRef, featuresVisible] = useFadeInOnScroll<HTMLElement>({
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
    triggerOnce: true,
    reverseOnExit: false,
    delay: 0,
    direction: 'up',
    distance: 40,
  });

  const features = [
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: t('features.items.ai.title'),
      description: t('features.items.ai.description'),
      detail: t('features.items.ai.detail')
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: t('features.items.automation.title'),
      description: t('features.items.automation.description'),
      detail: t('features.items.automation.detail')
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
        </svg>
      ),
      title: t('features.items.analytics.title'),
      description: t('features.items.analytics.description'),
      detail: t('features.items.analytics.detail')
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: t('features.items.integrations.title'),
      description: t('features.items.integrations.description'),
      detail: t('features.items.integrations.detail')
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: t('features.items.security.title'),
      description: t('features.items.security.description'),
      detail: t('features.items.security.detail')
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('features.items.support.title'),
      description: t('features.items.support.description'),
      detail: t('features.items.support.detail')
    }
  ];

  return (
    <FeaturesSection ref={featuresRef} id="features" $isVisible={featuresVisible}>
      <Container>
        <SectionHeader>
          <EnhancedSectionTitle>{t('features.title')}</EnhancedSectionTitle>
          <SectionSubtitle>
            {t('features.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <PowerfulFeaturesContainer>
          <FeaturedDisplay feature={features[activeFeature]} />
          <FeaturesList
            features={features}
            activeFeature={activeFeature}
            hoveredFeature={hoveredFeature}
            onFeatureClick={setActiveFeature}
            onFeatureHover={setHoveredFeature}
          />
        </PowerfulFeaturesContainer>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
